import React, { useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Alert,
  AlertTitle,
  Skeleton,
  Stack,
  Divider,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

// Hooks
import { useFinancialDashboardState } from '../../hooks/useFinancialDashboardState';
import {
  useFinancialDashboard,
  useFinancialProperties,
  GetDashboardDataParams,
} from '../../hooks/api/useFinancialData';
import { useAuth } from '../../contexts/AuthContext';
import { useRenderPerformance, useMemoryMonitoring } from '../../hooks/usePerformanceMonitoring';
import { useFinancialDataCache } from '../../hooks/useFinancialDataCache';
import { useFinancialDashboardData } from '../../hooks/useFinancialApiWithRetry';
import { useFinancialMonitoring } from '../../utils/financial-monitoring';


// Components
import FinancialDashboardErrorBoundary from './FinancialDashboardErrorBoundary';
import FinancialErrorDisplay from './FinancialErrorDisplay';
import { PropertySelector } from './PropertySelector';
import { DateRangePicker } from './DateRangePicker';
import { MetricsCards } from './MetricsCards';
import { LazyRevenueChart, LazyExpenseChart, LazyProfitLossChart } from './Charts';


// Types
import { DateRange, Property } from '../../types';

export interface FinancialDashboardProps {
  defaultProperties?: string[];
  defaultDateRange?: DateRange;
  className?: string;
}

const FinancialDashboardContent: React.FC<FinancialDashboardProps> = ({
  defaultProperties = [],
  defaultDateRange,
  className,
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Performance monitoring
  useRenderPerformance('FinancialDashboard', {
    defaultProperties,
  });
  useMemoryMonitoring('FinancialDashboard');

  // Financial monitoring
  const {
    recordInteraction,
    recordError,
    measureAsync,
    startTiming
  } = useFinancialMonitoring();

  // Data caching
  const { loadDashboardPreferences, saveDashboardPreferences } = useFinancialDataCache();

  // Dashboard state management
  const {
    state,
    actions,
    computed,
    helpers,
  } = useFinancialDashboardState({
    defaultProperties,
    defaultDateRange,
  });

  // Enhanced financial API with retry logic
  const dashboardApi = useFinancialDashboardData({
    maxRetries: 0, // No retries
    enableAutoRetry: false,
    onSuccess: (data) => {
      recordInteraction({
        action: 'dashboard_data_loaded',
        component: 'FinancialDashboard',
        metadata: {
          expenseCount: data?.expenseData?.length || 0,
          revenueCount: data?.revenueData?.length || 0,
          propertiesCount: state.selectedProperties.length,
          dateRange: helpers.getDateRangeLabel()
        }
      });
    },
    onError: (error) => {
      recordError({
        type: error.type,
        message: error.message,
        severity: error.severity || 'medium',
        context: {
          component: 'FinancialDashboard',
          operation: 'fetch_dashboard_data',
          selectedProperties: state.selectedProperties,
          dateRange: state.dateRange
        }
      });
    }
  });

  // Fetch user properties
  const {
    data: properties = [],
    isLoading: propertiesLoading,
    error: propertiesError,
  } = useFinancialProperties();



  // Auto-select all properties if none selected and properties are loaded
  useEffect(() => {
    if (
      properties.length > 0 &&
      state.selectedProperties.length === 0 &&
      defaultProperties.length === 0
    ) {
      const endTiming = startTiming('auto_select_properties');
      actions.setSelectedProperties(properties.map(p => p.id));
      endTiming();

      recordInteraction({
        action: 'auto_select_properties',
        component: 'FinancialDashboard',
        metadata: { propertiesCount: properties.length }
      });
    }
  }, [properties, state.selectedProperties.length, defaultProperties.length, actions, startTiming, recordInteraction]);

  // Prepare dashboard data query parameters
  const dashboardParams: GetDashboardDataParams = useMemo(() => {
    if (!state.dateRange?.startDate || !state.dateRange?.endDate) {
      return {
        propertyIds: [],
        startDate: '',
        endDate: '',
        groupBy: 'day' as const,
      };
    }

    const params = {
      propertyIds: state.selectedProperties,
      startDate: state.dateRange.startDate.toISOString().split('T')[0],
      endDate: state.dateRange.endDate.toISOString().split('T')[0],
      groupBy: 'day' as const, // Default grouping
    };

    // Debug log to verify date range
    console.log('Dashboard API params:', {
      ...params,
      preset: state.dateRange.preset,
      dateRangeLabel: helpers.getDateRangeLabel()
    });

    return params;
  }, [state.selectedProperties, state.dateRange, helpers]);

  // Dashboard data fetching - supports both automatic and manual loading
  const loadDashboardData = useCallback(() => {
    if (computed.canLoadData && state.selectedProperties.length > 0 && state.dateRange?.startDate && state.dateRange?.endDate) {
      const dateRangeDays = Math.ceil((state.dateRange.endDate.getTime() - state.dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24));

      measureAsync(
        'fetch_dashboard_data',
        () => dashboardApi.fetchDashboardData(dashboardParams),
        {
          propertiesCount: state.selectedProperties.length,
          dateRangeDays
        }
      ).catch(error => {
        // Error is already handled by the API hook
        console.error('Dashboard data fetch failed:', error);
      });
    }
  }, [dashboardParams, computed.canLoadData, state.selectedProperties.length, dashboardApi, measureAsync, state.dateRange]);

  // Auto-load dashboard data when filters are ready
  useEffect(() => {
    if (computed.canLoadData && state.selectedProperties.length > 0 && !dashboardApi.state.data && !dashboardApi.state.loading) {
      const endTiming = startTiming('auto_load_dashboard_data');

      recordInteraction({
        action: 'auto_load_dashboard_data',
        component: 'FinancialDashboard',
        metadata: {
          propertiesCount: state.selectedProperties.length,
          dateRange: helpers.getDateRangeLabel()
        }
      });

      loadDashboardData();
      endTiming();
    }
  }, [computed.canLoadData, state.selectedProperties.length, dashboardApi.state.data, dashboardApi.state.loading, loadDashboardData, startTiming, recordInteraction, helpers]);

  // Handle errors with enhanced error display
  const hasError = propertiesError || dashboardApi.state.error;
  const isLoading = propertiesLoading || dashboardApi.state.loading || state.loading;
  const dashboardData = dashboardApi.state.data;





  // No properties available
  if (!propertiesLoading && properties.length === 0) {
    return (
      <Box sx={{ py: 4 }} className={className}>
        <Alert severity="info">
          <AlertTitle>No Properties Found</AlertTitle>
          You don't have any properties set up yet. Create a property first to view financial data.
        </Alert>
      </Box>
    );
  }

  // Enhanced error state with financial error display
  if (hasError && !isLoading) {
    return (
      <Box sx={{ py: 4 }} className={className}>
        {dashboardApi.state.error && (
          <FinancialErrorDisplay
            error={dashboardApi.state.error}
            onRetry={() => {
              recordInteraction({
                action: 'error_retry_clicked',
                component: 'FinancialDashboard',
                metadata: { errorType: dashboardApi.state.error?.type }
              });
              dashboardApi.retry();
            }}
            onDismiss={() => {
              recordInteraction({
                action: 'error_dismissed',
                component: 'FinancialDashboard',
                metadata: { errorType: dashboardApi.state.error?.type }
              });
              dashboardApi.clearError();
            }}
            showDetails={process.env.NODE_ENV === 'development'}
          />
        )}

        {propertiesError && !dashboardApi.state.error && (
          <Alert severity="error">
            <AlertTitle>Error Loading Properties</AlertTitle>
            Failed to load properties. Please try refreshing the page.
          </Alert>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }} className={className}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant={isMobile ? 'h5' : 'h4'} component="h1">
              Financial Dashboard
            </Typography>
          </Stack>

          {/* Manual Refresh Button */}
          <Button
            variant="outlined"
            onClick={() => {
              recordInteraction({
                action: 'manual_refresh_triggered',
                component: 'FinancialDashboard',
                metadata: { timestamp: Date.now() }
              });
              loadDashboardData();
            }}
            disabled={isLoading}
            size="small"
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </Button>
        </Stack>

        <Typography variant="body1" color="text.secondary">
          Monitor your property performance with comprehensive revenue, expense, and profit analysis.
        </Typography>
      </Box>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>

        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={6}>
            <PropertySelector
              properties={properties}
              selectedProperties={state.selectedProperties}
              onSelectionChange={(newSelection) => {
                recordInteraction({
                  action: 'properties_selected',
                  component: 'PropertySelector',
                  metadata: {
                    previousCount: state.selectedProperties.length,
                    newCount: newSelection.length
                  }
                });
                actions.setSelectedProperties(newSelection);
              }}
              loading={propertiesLoading}
              error={propertiesError?.message}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            {state.dateRange && (
              <DateRangePicker
                dateRange={state.dateRange}
                onDateRangeChange={(newDateRange) => {
                  recordInteraction({
                    action: 'date_range_changed',
                    component: 'DateRangePicker',
                    metadata: {
                      startDate: newDateRange.startDate.toISOString().split('T')[0],
                      endDate: newDateRange.endDate.toISOString().split('T')[0],
                      daysDiff: Math.ceil((newDateRange.endDate.getTime() - newDateRange.startDate.getTime()) / (1000 * 60 * 60 * 24))
                    }
                  });
                  actions.setDateRange(newDateRange);
                }}
                presetOptions={computed.presetOptions}
                loading={isLoading}
                error={!computed.isValidDateRange ? 'Invalid date range' : undefined}
              />
            )}
          </Grid>


        </Grid>

        {/* Filter Summary and Load Button */}
        {computed.hasFilters && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={helpers.getSelectedPropertiesLabel(properties)}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={helpers.getDateRangeLabel()}
                  size="small"
                  variant="outlined"
                />

              </Stack>
              <Button
                variant="contained"
                onClick={loadDashboardData}
                disabled={!computed.canLoadData || isLoading}
                startIcon={<TrendingUpIcon />}
              >
                {isLoading ? 'Loading...' : 'Load Data'}
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Dashboard Content */}
      {!isLoading && computed.canLoadData && dashboardData && (
        <>
          {/* Metrics Cards */}
          <Box sx={{ mb: 4 }}>
            <MetricsCards
              metrics={dashboardData.metrics}
              lastUpdated={dashboardData.lastUpdated}
              loading={false}
            />
          </Box>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, display: 'flex', flexDirection: 'column', height: { xs: '300px', md: '400px' } }}>
                <Typography variant="h6" gutterBottom sx={{ flexShrink: 0, mb: 1 }}>
                  Revenue Trend
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                  <LazyRevenueChart
                    data={dashboardData.revenueData}
                    chartType="line"
                    groupBy="day"
                    height={320}
                    showControls={false}
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, display: 'flex', flexDirection: 'column', height: { xs: '300px', md: '400px' } }}>
                <Typography variant="h6" gutterBottom sx={{ flexShrink: 0, mb: 1 }}>
                  Expense Breakdown
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                  <LazyExpenseChart
                    data={dashboardData.expenseData}
                    chartType="pie"
                    height={320}
                    showControls={false}
                  />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3, display: 'flex', flexDirection: 'column', height: { xs: '300px', md: '400px' } }}>
                <Typography variant="h6" gutterBottom sx={{ flexShrink: 0, mb: 1 }}>
                  Profit & Loss Trend
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                  <LazyProfitLossChart
                    data={dashboardData.profitLossData}
                    showProjections={false}
                    height={320}
                    showControls={false}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>




        </>
      )}

      {/* No Data State */}
      {!isLoading && computed.canLoadData && !dashboardData && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <WarningIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Financial Data Found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No financial data available for the selected properties and date range.
            Try adjusting your filters or check back later.
          </Typography>
        </Box>
      )}

      {/* Invalid Filters State */}
      {!computed.canLoadData && !isLoading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto' }}>
            <AlertTitle>Invalid Filters</AlertTitle>
            Please select at least one property and ensure the date range is valid to view financial data.
          </Alert>
        </Box>
      )}
    </Box>
  );
};

// Main component with enhanced error boundary
export const FinancialDashboard: React.FC<FinancialDashboardProps> = (props) => {
  const { recordError, recordInteraction } = useFinancialMonitoring();

  return (
    <FinancialDashboardErrorBoundary
      onError={(error, errorInfo) => {
        recordError({
          type: 'COMPONENT_ERROR_BOUNDARY',
          message: error.message,
          severity: 'critical',
          context: {
            component: 'FinancialDashboard',
            errorName: error.name,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
            props
          }
        });
      }}
      onRetry={() => {
        recordInteraction({
          action: 'error_boundary_retry',
          component: 'FinancialDashboard',
          metadata: { timestamp: Date.now() }
        });
      }}
      showDetailedError={process.env.NODE_ENV === 'development'}
      enableErrorReporting={process.env.NODE_ENV === 'production'}
    >
      <FinancialDashboardContent {...props} />
    </FinancialDashboardErrorBoundary>
  );
};

export default FinancialDashboard;